import { setDailyStats } from "@lib/chart-utils";
import HttpClient from "@lib/http-client";

const defaultExportOptions = {
  createFile: true,
};

/**
 * @memberof Services
 */
export class OrganizationService extends HttpClient {
  getOrganizationDetails(orgCode) {
    const apiUrl = `/organization/${orgCode}`;
    return this.http.get(apiUrl);
  }

  async updateOrganization(orgCode, updates) {
    return await this.http.patch(`/organization/${orgCode}`, updates);
  }

  /**
   *
   * @param {string} orgCode
   * @returns {array}
   */
  getChildOrganizationStats(multiOrgCode) {
    const apiUrl = `/multi-organization/${multiOrgCode}/organizations/stats`;

    return this.http.get(apiUrl);
  }

  getMultiOrganizationStats(orgCode, filters) {
    const apiUrl = `/multi-organization/${orgCode}/stats`;
    return this.getGeneralStats(apiUrl, filters);
  }

  getMultiOrganizationCeStats(orgCode, filters) {
    const apiUrl = `/multi-organization/${orgCode}/ce/stats`;
    return this.getGeneralStats(apiUrl, filters);
  }

  getMultiOrganizationProgStats(orgCode, filters) {
    const apiUrl = `/multi-organization/${orgCode}/program/stats`;
    return this.getGeneralStats(apiUrl, filters);
  }

  exportMultiOrganizationCeStats(orgCode, filters, fileName, options) {
    const apiUrl = `/multi-organization/${orgCode}/ce/stats/export`;
    return this.exportStats(apiUrl, filters, fileName, options);
  }

  getMultiOrganizationDailyStats(orgCode, filters) {
    const apiUrl = `/multi-organization/${orgCode}/daily/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getMultiOrganizationDailyProgStats(orgCode, filters) {
    const apiUrl = `/multi-organization/${orgCode}/daily/program/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getMultiOrganizationDailyCeStats(orgCode, filters) {
    const apiUrl = `/multi-organization/${orgCode}/daily/ce/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  exportMultiOrganizationStats(orgCode, filters, fileName) {
    const apiUrl = `/multi-organization/${orgCode}/stats/export`;
    return this.exportStats(apiUrl, filters, fileName);
  }

  getOrganizationStats(orgCode, filters) {
    const apiUrl = `/organization/${orgCode}/stats`;
    return this.getGeneralStats(apiUrl, filters);
  }

  getOrganizationCeStats(orgCode, filters, options) {
    const apiUrl = `/organization/${orgCode}/ce/stats`;
    return this.getGeneralStats(apiUrl, filters, options);
  }

  getOrganizationProgStats(orgCode, filters) {
    const apiUrl = `/organization/${orgCode}/program/stats`;
    return this.getGeneralStats(apiUrl, filters);
  }

  getOrganizationDailyStats(orgCode, filters) {
    const apiUrl = `/organization/${orgCode}/daily/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getOrganizationDailyProgStats(orgCode, filters) {
    const apiUrl = `/organization/${orgCode}/daily/program/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getOrganizationDailyCeStats(orgCode, filters) {
    const apiUrl = `/organization/${orgCode}/daily/ce/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  exportOrganizationStats(orgCode, filters, fileName) {
    const apiUrl = `/organization/${orgCode}/stats/export`;
    return this.exportStats(apiUrl, filters, fileName);
  }

  exportOrganizationCeStats(orgCode, filters, fileName, options) {
    const apiUrl = `/organization/${orgCode}/ce/stats/export`;
    return this.exportStats(apiUrl, filters, fileName, options);
  }

  getInstructorStats(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/stats/`;
    return this.getGeneralStats(apiUrl, filters).then((response) =>
      response?.pop()
    );
  }

  getInstructorCeStats(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/ce/stats`;
    return this.getGeneralStats(apiUrl, filters).then((response) =>
      response?.pop()
    );
  }

  getInstructorProgStats(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/program/stats`;
    return this.getGeneralStats(apiUrl, filters).then((response) => response);
  }

  exportInstructorStats(instructorId, filters, fileName) {
    const apiUrl = `/user/instructor/${instructorId}/stats/export`;
    return this.exportStats(apiUrl, filters, fileName);
  }

  exportInstructorCeStats(instructorId, filters, fileName, options) {
    const apiUrl = `/user/instructor/${instructorId}/ce/stats/export`;
    return this.exportStats(apiUrl, filters, fileName, options);
  }

  getInstructorDailyStats(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/daily/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getInstructorDailyCeStats(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/daily/ce/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getInstructorDailyProgStats(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/daily/program/stats/`;
    return this.getDailyStats(apiUrl, filters);
  }

  getGeneralStats(api, filters) {
    return this.http.post(api, { filters });
  }

  getDailyStats(api, filters) {
    return this.http
      .post(api, { filters })
      .then((response) => setDailyStats(filters, response));
  }

  getTopCareers(api, filters) {
    return this.http.post(api, { filters }).catch((error) => {
      console.error(`[Insights Service] - An error occured: ${error}`);
      return [];
    });
  }

  getInstructorTopCareers(instructorId, filters) {
    const apiUrl = `/user/instructor/${instructorId}/ce/top-careers`;
    return this.getTopCareers(apiUrl, filters);
  }

  getOrganizationTopCareers(orgCode, filters) {
    const apiUrl = `organization/${orgCode}/ce/top-careers`;
    return this.getTopCareers(apiUrl, filters);
  }

  getMultiOrganizationTopCareers(orgCode, filters) {
    const apiUrl = `multi-organization/${orgCode}/ce/top-careers`;
    return this.getTopCareers(apiUrl, filters);
  }

  /**
   * `getTopCareers` turns a failed request into an empty list, which makes a
   * server error indistinguishable from "nobody rated it". `rejectOnError`
   * posts directly instead, so a caller rendering an error state sees the
   * rejection.
   */
  careerRatings(api, filters, rejectOnError) {
    return rejectOnError
      ? this.http.post(api, { filters })
      : this.getTopCareers(api, filters);
  }

  getInstructorCareerRatings(
    instructorId,
    learningExperienceId,
    filters,
    rejectOnError
  ) {
    const apiUrl = `/user/instructor/${instructorId}/ce/career/${learningExperienceId}/ratings`;
    return this.careerRatings(apiUrl, filters, rejectOnError);
  }

  getOrganizationCareerRatings(
    orgCode,
    learningExperienceId,
    filters,
    rejectOnError
  ) {
    const apiUrl = `organization/${orgCode}/ce/career/${learningExperienceId}/ratings`;
    return this.careerRatings(apiUrl, filters, rejectOnError);
  }

  getMultiOrganizationCareerRatings(
    orgCode,
    learningExperienceId,
    filters,
    rejectOnError
  ) {
    const apiUrl = `multi-organization/${orgCode}/ce/career/${learningExperienceId}/ratings`;
    return this.careerRatings(apiUrl, filters, rejectOnError);
  }

  exportInstructorProgStats(instructorId, filters, fileName, options) {
    const apiUrl = `/user/instructor/${instructorId}/program/stats/export`;
    return this.exportStats(apiUrl, filters, fileName, options);
  }

  exportMultiOrganizationProgStats(orgCode, filters, fileName, options) {
    const apiUrl = `/multi-organization/${orgCode}/program/stats/export`;
    return this.exportStats(apiUrl, filters, fileName, options);
  }

  exportOrganizationProgStats(orgCode, filters, fileName, options) {
    const apiUrl = `/organization/${orgCode}/program/stats/export`;
    return this.exportStats(apiUrl, filters, fileName, options);
  }

  exportStats(api, filters, fileName, exportOptions) {
    const requestConfig = {
      method: "post",
      url: api,
      data: { filters },
    };
    const options = { ...defaultExportOptions, ...exportOptions };

    return options.createFile
      ? this.downloadBlob(requestConfig, fileName, "csv")
      : this.parseCsvResponse(requestConfig);
  }

  async getChildOrganizations(multiOrgCode) {
    const apiUrl = `/multi-organization/${multiOrgCode}/organizations`;

    const data = await this.http.get(apiUrl);
    return data;
  }

  async getOrganizationCustomCategories(orgCode) {
    const apiUrl = `/organization/${orgCode}/custom_categories`;

    const data = await this.http.get(apiUrl);
    return data;
  }

  async getMultiOrganizationCustomCategories(orgCode) {
    const apiUrl = `/multi-organization/${orgCode}/custom_categories`;

    const data = await this.http.get(apiUrl);
    return data;
  }

  async getOrganizationHeadsetDevices(orgCode) {
    const apiUrl = `/organization/${orgCode}/headset-devices`;

    const data = await this.http.get(apiUrl);
    return data;
  }

  async getDeviceConfigurations(orgCode, configurationIds) {
    const apiUrl = `/organization/${orgCode}/devices-configurations`;

    const data = await this.http.post(apiUrl, configurationIds);
    return data;
  }

  getDeviceLastOnlineEnabled() {
    return this.http.get("/organization/devices/feature-enabled");
  }

  getProgramsFeatureEnabled() {
    return this.http.get("/organization/programs/feature-enabled");
  }

  getOrganizationProgramTemplates(orgCode, { selected } = {}) {
    const params = selected === undefined ? undefined : { selected };
    return this.http.get(`/organization/${orgCode}/programs`, { params });
  }

  async getDashboardConfigs() {
    const apiUrl = `/organization/settings/dashboard-configs`;
    const data = await this.http.get(apiUrl);
    return data;
  }

  async getOrganizationDeviceCount(orgCode) {
    const apiUrl = `/organization/${orgCode}/device-count`;
    return await this.http.get(apiUrl);
  }

  async getOrganizationTags(orgCode, tagType) {
    return await this.http.get(`/organization/${orgCode}/tags`, {
      params: { type: tagType },
    });
  }

  async createOrganizationTag(orgCode, tag) {
    return await this.http.post(`/organization/${orgCode}/tags`, tag);
  }

  async deleteOrganizationTag(orgCode, tagId) {
    return await this.http.delete(`/organization/${orgCode}/tags/${tagId}`);
  }

  async updateOrganizationTag(orgCode, tag) {
    return await this.http.patch(
      `/organization/${orgCode}/tags/${tag.id}`,
      tag
    );
  }

  async setDeviceTags(orgCode, deviceId, tagIds) {
    const apiUrl = `/organization/${orgCode}/devices/${deviceId}/tags`;
    return await this.http.put(apiUrl, { tagIds });
  }
}
